import React, { useState } from "react";
import { Layout, Menu, Breadcrumb } from "antd";
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';

const { Content, Sider } = Layout;

const graphs = [
  {
    id: "graph1",
    title: "Evolución en el tiempo",
    menuTitle: "Evolución en el tiempo",
    text: `En Chile, un 26% de las niñas y los niños vive en hogares donde los adultos no tienen un empleo seguro. Es decir, no cuentan con empleo formal y permanente. Esto representa a cerca de 1.073.354 niñas 
          y niños del país. En 2011, la proporción de este indicador era de un 34%, entre 2013 y 2022 la proporción se mantuvo constante alrededor del 30%, y con la última medición se registró una baja al 26%.`,
    link: "https://public.flourish.studio/visualisation/20935983/embed",
  },
  {
    id: "graph2",
    title: "Análisis por sexo",
    menuTitle: "\u2022 Sexo",
    text: `Tanto niñas como niños tienen las mismas probabilidades de vivir en hogares cuyos adultos no tienen un empleo seguro.`,
    link: "https://public.flourish.studio/visualisation/20935985/embed",
  },
  {
    id: "graph3",
    title: "Análisis por edad",
    menuTitle: "\u2022 Edad",
    text: `A diferencia de lo observado en mediciones anteriores, los datos de 2024 muestran que las diferencias en la proporción de niñas y niños con cuidadores en empleos inseguros son estadísticamente 
          significativas entre ciertos tramos. Específicamente, el tramo de primera infancia presenta una prevalencia menor en comparación con el grupo de adolescentes (25% y 27% respectivamente).`,
    link: "https://public.flourish.studio/visualisation/20935991/embed",
  },
  {
    id: "graph4",
    title: "Análisis por país de nacimiento",
    menuTitle: "\u2022 País de nacimiento",
    text: `En 2024, se mantiene la tendencia observada desde 2022 de que las niñas y los niños que nacieron fuera de Chile viven en hogares cuyos cuidadores no tienen empleos seguros en mayor proporción que las 
          niñas y niños que nacieron en el país (35% versus 25%). Sin embargo, para los años anteriores no se observan diferencias estadísticamente significativas, lo que sugiere que la población que nació fuera 
          de Chile se ha insertado en los últimos años de manera más precaria a la estructura laboral del país.`,
    link: "https://public.flourish.studio/visualisation/20935994/embed",
  },
  {
    id: "graph5",
    title: "Análisis por pueblo originario",
    menuTitle: "\u2022 Pueblo originario",
    text: `Las niñas y los niños que se identifican con un pueblo originario son más vulnerables en este indicador que quienes no lo hacen. Mientras un 32% de ellas y ellos vive en hogares cuyos cuidadores no 
          tienen un empleo seguro en la última medición, esta proporción es del 25% entre quienes no pertenecen a pueblos indígenas. Esta brecha se replica en todos los años de medición, pero ha disminuido.`,
    link: "https://public.flourish.studio/visualisation/20936000/embed",
  },
  {
    id: "graph6",
    title: "Análisis por quintil de ingresos",
    menuTitle: "\u2022 Quintil de ingresos",
    text: `El nivel de ingresos del hogar está estrechamente vinculado a la seguridad del empleo. En 2024, entre las niñas y los niños que viven en hogares del primer quintil de ingresos, el 57% tiene padres o 
          cuidadores sin empleo seguro. Esta proporción disminuye de forma sostenida a medida que aumentan los ingresos: bajando al 28% en el segundo quintil, 15% en el tercero, 9% en el cuarto y llegando a un 
          5% en el quinto quintil.

  Al observar la evolución histórica desde 2011, se evidencia una mejora estructural en los sectores medios: los hogares de los quintiles II, III y IV han experimentado una disminución estadísticamente significativa 
          en sus niveles de inseguridad laboral al llegar a 2024. Por el contrario, la situación de las niñas y niños en el primer quintil ha sido inestable; aunque la proporción de empleos inseguros mostró caídas 
          entre 2013 y 2015, los niveles volvieron a aumentar en las mediciones posteriores. Así, en 2024, la inseguridad laboral en este grupo de menores ingresos no presenta diferencias estadísticamente significativas 
          respecto a la observada al inicio de la serie en 2011.

  El acceso de los cuidadores a empleos seguros es fundamental para garantizar estabilidad en los ingresos familiares.`,
    link: "https://public.flourish.studio/visualisation/20936007/embed",
  },
  {
    id: "graph7",
    title: "Análisis por zona de residencia",
    menuTitle: "\u2022 Zona",
    text: `En las zonas rurales existe una mayor proporción de niñas y niños en hogares sin empleo seguro. En 2024, un 40% de las niñas y los niños de zonas rurales vive en hogares sin empleo seguro, versus un 
          25% de sus pares de zonas urbanas. Esta brecha se ha mantenido constante en el tiempo.`,
    link: "https://public.flourish.studio/visualisation/20935998/embed",
  },
  {
    id: "graph8",
    title: "Análisis por macrozona",
    menuTitle: "\u2022 Macrozona",
    text: `Fuera de la Región Metropolitana (RM), las niñas y los niños tienen una mayor probabilidad de vivir en hogares sin empleo seguro. Sin embargo, la medición de 2024 muestra cambios importantes en las 
          tendencias de todas las zonas. En la Macrozona Norte (28%), el deterioro que se venía observando desde 2015 se revirtió, mostrando una caída significativa respecto a 2022 que devuelve el indicador a 
          niveles similares a los de hace casi una década. Por su parte, las Macrozonas Centro y Sur (ambas 30%) consolidan su disminución histórica iniciada en 2011; destacando la zona Centro con una nueva 
          baja significativa en el último período. Finalmente, en la Región Metropolitana (21%) —que se había mantenido constante históricamente— se registró en 2024 una caída estadísticamente significativa en 
          la proporción de hogares con empleo inseguro, alcanzando su nivel más bajo de la serie.`,
    link: "https://public.flourish.studio/visualisation/20936013/embed",
  },
  {
    id: "graph9",
    title: "Análisis por región",
    menuTitle: "\u2022 Región",
    text: `Al comparar con la medición de 2022, el panorama general del 2024 muestra una estabilización estadística en la mayor parte del país. Sin embargo, destacan positivamente Valparaíso (26%), la Región 
          Metropolitana (21%) y el Maule (34%), las cuales lograron disminuciones estadísticamente significativas en este período. En particular, la región del Maule, a pesar de esta importante mejora, se mantiene 
          dentro del grupo de mayor vulnerabilidad a nivel nacional. Dentro de este grupo se encuentran, además, las regiones de Ñuble (36%), La Araucanía (36%), Tarapacá (33%), Arica y Parinacota (30%) y 
          Coquimbo (30%). Estas regiones coinciden, por un lado, con las del extremo norte del país y, por otro, con las de la zona centro-sur, donde hay una mayor cantidad de niñas y niños viviendo en zonas rurales.`,
    link: "https://public.flourish.studio/visualisation/21575348/embed",
  }
];

const App = () => {
  const [collapsed, setCollapsed] = useState(false);
  const [selectedMenuItem, setSelectedMenuItem] = useState("graph1");

  const handleMenuItemClick = (graphId, graphTitle) => {
    setSelectedMenuItem(graphId);
  };

  const toggleCollapsed = () => {
    setCollapsed(!collapsed);
  };

  const selectedGraph = graphs.find((graph) => graph.id === selectedMenuItem);

  return (
    <Layout style={{ minHeight: "100vh" }}>
      <Sider
        width={250}
        collapsible
        collapsed={collapsed}
        onCollapse={toggleCollapsed}
        style={{ background: "#fff" }}
        breakpoint="lg"
        collapsedWidth={0}
      >
        <Menu theme="dark" mode="vertical" selectedKeys={[selectedMenuItem]} style={{ background: "#fff" }}>
          {graphs.map((graph) => (
            <Menu.Item
              key={graph.id}
              onClick={() => handleMenuItemClick(graph.id)}
              style={{ color: 'black', paddingLeft: graph.id !== "graph1" ? "30px" : "20px" }}
            >
              {graph.menuTitle}
            </Menu.Item>
          ))}
        </Menu>
      </Sider>
      <Layout style={{ padding: "24px 24px 24px" }}>
        <Breadcrumb style={{ margin: "16px 0" }}>
          <Breadcrumb.Item></Breadcrumb.Item>
        </Breadcrumb>
        <Content
          id="content"
          style={{
            background: "#ffff",
            padding: 24,
            margin: 0,
            minHeight: 280,
          }}
        >
          {selectedGraph && (
            <div>
              <div style={{ marginBottom: 16 }}>
                <h1>{selectedGraph.title}</h1>
              </div>
              <div style={{ textAlign: 'justify' }}>
                <ReactMarkdown remarkPlugins={[remarkGfm]}>
                  {selectedGraph.text}
                </ReactMarkdown>
              </div>
              <iframe
                src={selectedGraph.link}
                frameBorder="0"
                scrolling="no"
                style={{ minWidth: "100%", height: "500px" }}
              ></iframe>
            </div>
          )}
        </Content>
      </Layout>
    </Layout>
  );
};

export default App;

